import {
  type CohortBuilderDefinitionLegacy,
  CohortBuilderItemChangedBy,
  CohortBuilderItemChangeStatusLegacy,
  type SubjectVariableLegacy,
  type Criterion,
} from '@verantos-dev/types-cohorts';

import { computeDiffForCohortBuilderItem } from '../computeDiffForCohortBuilderItem';

export const getFullCohortDslForSubjectsVariables = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  nextSubjectVariable: SubjectVariableLegacy,
  addChangeStatus = false
) => {
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
    (subjectVariable) => subjectVariable.id === nextSubjectVariable.id
  );

  const nextSubjectVariables =
    payloadSubjectVariableIndex > -1
      ? currentSubjectVariables.map((subjectVariable) => {
          if (subjectVariable.id === nextSubjectVariable.id) {
            if (addChangeStatus) {
              let _changeStatus: CohortBuilderItemChangeStatusLegacy | undefined;
              if (computeDiffForCohortBuilderItem(subjectVariable, nextSubjectVariable)) {
                // if subject variable previously existed and is different from current update
                if (subjectVariable.changeStatus === CohortBuilderItemChangeStatusLegacy.NEW) {
                  // if subject variable was new, then it's still new
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.NEW;
                } else {
                  // subject variable is edited
                  _changeStatus = CohortBuilderItemChangeStatusLegacy.EDITED;
                }
              } else {
                // if subject variable previously existed and is the same as current update, then it's unchanged
                _changeStatus = subjectVariable.changeStatus;
              }

              return {
                ...nextSubjectVariable,
                changeStatus: _changeStatus,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              };
            } else {
              return nextSubjectVariable;
            }
          }
          return subjectVariable;
        })
      : [
          ...currentSubjectVariables,
          addChangeStatus
            ? {
                ...nextSubjectVariable,
                changeStatus: CohortBuilderItemChangeStatusLegacy.NEW,
                changedBy: CohortBuilderItemChangedBy.COHORT_BUILDER,
              }
            : nextSubjectVariable,
        ];

  let nextCriteria: Criterion[] | undefined;
  const currentCriteria = currentCohortDefinition?.criteria;

  if (currentCriteria !== undefined) {
    const existingCriterionIndex = currentCriteria.findIndex(
      (criterion): criterion is Criterion =>
        criterion !== null &&
        typeof criterion === 'object' &&
        'criteriaType' in criterion &&
        criterion.criteriaType === 'subject_variable' &&
        'subjectVariableId' in criterion &&
        criterion.subjectVariableId === nextSubjectVariable.id
    );

    const subjectVariableCriterion: Criterion = {
      criteriaType: 'subject_variable',
      subjectVariableId: nextSubjectVariable.id,
    };

    if (existingCriterionIndex > -1) {
      nextCriteria = currentCriteria.map((criterion, index) =>
        index === existingCriterionIndex ? subjectVariableCriterion : criterion
      );
    } else {
      nextCriteria = [...currentCriteria, subjectVariableCriterion];
    }
  } else {
    nextCriteria = undefined;
  }

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    subjectVariables: nextSubjectVariables,
    indexDate: currentCohortDefinition?.indexDate,
    simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
    derivedEvents: currentCohortDefinition?.derivedEvents ?? [],
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: nextCriteria,
  };

  return {
    cohortDefinition,
  };
};

export const getFullCohortDslForDeleteSubjectsVariables = (
  currentCohortDefinition: CohortBuilderDefinitionLegacy | undefined,
  id: string
) => {
  const currentSubjectVariables = currentCohortDefinition?.subjectVariables ?? [];
  const payloadSubjectVariableIndex = currentSubjectVariables.findIndex(
    (subjectVariable) => subjectVariable.id === id
  );

  const nextSubjectVariables = [
    ...currentSubjectVariables.slice(0, payloadSubjectVariableIndex),
    ...currentSubjectVariables.slice(payloadSubjectVariableIndex + 1),
  ];

  let nextCriteria: Criterion[] | undefined;
  const currentCriteria = currentCohortDefinition?.criteria;

  if (currentCriteria !== undefined) {
    nextCriteria = currentCriteria.filter(
      (criterion) =>
        !(
          criterion !== null &&
          typeof criterion === 'object' &&
          'criteriaType' in criterion &&
          criterion.criteriaType === 'subject_variable' &&
          'subjectVariableId' in criterion &&
          criterion.subjectVariableId === id
        )
    );
  } else {
    nextCriteria = undefined;
  }

  const cohortDefinition: CohortBuilderDefinitionLegacy = {
    studyPeriodRestrictions: currentCohortDefinition?.studyPeriodRestrictions,
    indexDate: currentCohortDefinition?.indexDate,
    subjectVariables: nextSubjectVariables,
    simpleEvents: currentCohortDefinition?.simpleEvents ?? [],
    derivedEvents: currentCohortDefinition?.derivedEvents ?? [],
    analyticalVariables: currentCohortDefinition?.analyticalVariables,
    criteria: nextCriteria,
  };

  return {
    cohortDefinition,
  };
};
